
import Vue from 'vue';
import { mapState, mapActions } from 'vuex';
import { db, FieldValue } from '@/firebase';
import BaseToggle from '@/components/base/BaseToggle.vue';
import BackButton from '@/components/base/BackButton.vue';
import NumberField from '@/components/base/NumberField.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { ROUTER } from '@/router/constants';
import _isNil from 'lodash/isNil';
import _sortBy from 'lodash/sortBy';
import {
	RunnitNodeDef,
	hundredthsToTokens,
	tokensToHundredths,
	asyncForEach,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	ProviderMapping,
	RunnitNodeDefProvider,
	RunnitPrivateNodeDef,
} from '@run-diffusion/shared';
import ClipboardCopy from '@/components/ClipboardCopy.vue';
import BigNumber from 'bignumber.js';

interface NodeDefGroup {
	provider: string;
	endpointId: string;
	nodeDefs: RunnitNodeDef[];
}

interface LogicEdits {
	[nodeDefId: string]: {
		[logicIndex: number]: {
			[fieldDefUuid: string]: {
				onValue: any;
				operator: string;
				operand: number;
				saving: boolean;
				changed: boolean;
			}
		}
	}
}

interface IndividualNodeDefEdits {
	[nodeDefId: string]: {
		editingCost: boolean;
		editCostValue?: number;
		hasCostChanges: boolean;
		editingTime: boolean;
		editTimeValue?: number;
		hasTimeChanges: boolean;
		// undefined = no pending change, true = pending publish, false = pending unpublish.
		// Check it against undefined, never truthiness, or a pending unpublish reads as no change.
		editPublishedValue?: boolean;
		isSaving: boolean;
	}
}

export default Vue.extend({
	name: 'AdminBulkEditNodeDefs',
	metaInfo: {
		title: 'Bulk Edit Runnit Tools | Admin | RunDiffusion',
	},
	components: {
		BackButton,
		NumberField,
		BaseToggle,
		BaseButton,
		ClipboardCopy,
	},
	data () {
		return {
			RUNNIT_NODE_DEF_TOOL_APP_TYPE,
			formValid: false,
			loading: true,
			isSaving: false,
			nodeDefs: [] as RunnitNodeDef[],
			groupedNodeDefs: {} as Record<string, Record<string, RunnitNodeDef[]>>,
			editFlags: {} as Record<string, Record<string, boolean>>,
			tokensPerResult: {} as Record<string, Record<string, number>>,
			originalTokensPerResult: {} as Record<string, Record<string, number>>,
			logicEdits: {} as LogicEdits,
			individualNodeDefEdits: {} as IndividualNodeDefEdits,
			BATCH_SIZE: 500, // Fetch 500 at a time

			// Pricing constants
			INTERNAL_PRICING_RUNNIT_TOKENS_MARGIN_MULTIPLE: 1.30,
			INTERNAL_PRICING_RUNNIT_TOKENS_CONVERSION_MULTIPLE: 0.0006,

			// Dollar values for bulk edit
			dollarsPerResult: {} as Record<string, Record<string, number>>,

			// Dollar values for individual edits
			individualDollarsPerResult: {} as Record<string, number>,
		};
	},
	computed: {
		...mapState(['user']),

		sortedGroupedNodeDefs (): NodeDefGroup[] {
			const result: NodeDefGroup[] = [];

			// Convert the nested structure to a flat array of groups
			const sortedProviders: string[] = _sortBy(Object.keys(this.groupedNodeDefs));
			sortedProviders.forEach((provider: string) => {
				const sortedEndpointIds: string[] = _sortBy(Object.keys(this.groupedNodeDefs[provider]));
				sortedEndpointIds.forEach((endpointId: string) => {
					result.push({
						provider,
						endpointId,
						nodeDefs: this.groupedNodeDefs[provider][endpointId],
					});
				});
			});

			return result;
		},

		hasChanges (): boolean {
			// Check if any changes have been made
			for (const provider in this.tokensPerResult) {
				for (const endpointId in this.tokensPerResult[provider]) {
					if (
						this.editFlags[provider]?.[endpointId] &&
						this.tokensPerResult[provider][endpointId] !== this.originalTokensPerResult[provider][endpointId]
					) {
						return true;
					}
				}
			}
			return Object.values(this.individualNodeDefEdits).some((edits: IndividualNodeDefEdits[string]) => edits.editPublishedValue !== undefined);
		}
	},
	async mounted () {
		await this.initialize();
	},
	methods: {
		...mapActions(['updateSnackbar']),
		_isNil,
		hundredthsToTokens,
		tokensToHundredths,
		goBack () {
			if (window.history.length > 2) {
				this.$router.back();
			} else {
				this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
			}
		},

		async initialize () {
			this.nodeDefs = [];
			this.groupedNodeDefs = {};
			this.editFlags = {};
			this.tokensPerResult = {};
			this.originalTokensPerResult = {};
			this.logicEdits = {};
			this.individualNodeDefEdits = {};
			this.loading = true;

			await this.fetchAllNodeDefs();
			await this.groupNodeDefs();
			this.initializeTokensPerResult();
			this.loading = false;
		},

		async fetchAllNodeDefs () {
			this.nodeDefs = [];
			let lastDoc = null;
			let hasMoreDocs = true;

			while (hasMoreDocs) {
				let query = db.collection('runnitNodeDefs')
					.where('isDeleted', '==', false)
					.limit(this.BATCH_SIZE);

				if (lastDoc) {
					query = query.startAfter(lastDoc);
				}

				const snapshot = await query.get();

				if (snapshot.empty || snapshot.docs.length < this.BATCH_SIZE) {
					hasMoreDocs = false;
				}

				const fetchedNodeDefs = snapshot.docs.map(doc => ({
					...doc.data(),
					get id () { return doc.id; },
				})) as RunnitNodeDef[];

				this.nodeDefs.push(...fetchedNodeDefs);

				if (snapshot.docs.length > 0) {
					lastDoc = snapshot.docs[snapshot.docs.length - 1];
				}
			}
		},

		async groupNodeDefs () {
			const groupedByProvider: Record<string, Record<string, RunnitNodeDef[]>> = {};

			// Process in batches for quicker processing
			const BATCH_SIZE: number = 100;
			const batches: RunnitNodeDef[][] = [];

			// Split nodeDefs into batches
			for (let i = 0; i < this.nodeDefs.length; i += BATCH_SIZE) {
				batches.push(this.nodeDefs.slice(i, i + BATCH_SIZE));
			}

			// Process each batch
			for (const batch of batches) {
				// Create an array of promises for this batch
				const promises = batch.map(async (nodeDef: RunnitNodeDef) => {
					try {
						// Initialize edit state for this node def
						this.individualNodeDefEdits[nodeDef.id] = {
							editingCost: false,
							editCostValue: undefined,
							hasCostChanges: false,
							editingTime: false,
							editTimeValue: undefined,
							hasTimeChanges: false,
							isSaving: false
						};

						// Fetch the corresponding private node def
						const privateNodeDefDoc = await db.collection('runnitPrivateNodeDefs').doc(nodeDef.id).get();

						if (!privateNodeDefDoc.exists) {
							console.warn(`No private node def found for ${nodeDef.id}`);
							return;
						}

						const privateNodeDef: RunnitPrivateNodeDef = privateNodeDefDoc.data() as RunnitPrivateNodeDef;
						const primaryProviderMapping: ProviderMapping = privateNodeDef?.providerMappings?.[0] || null;
						const provider: RunnitNodeDefProvider = primaryProviderMapping?.provider;
						const endpointId: string = primaryProviderMapping?.endpointId;

						// Check if provider and endpointId exist on the private node def
						if (!provider || !endpointId) return;

						return {
							nodeDef,
							provider,
							endpointId,
						};
					} catch (e) {
						console.error(`Error fetching private node def for ${nodeDef.id}:`, e);
						return null;
					}
				});

				// Wait for all promises in this batch to resolve
				const results = await Promise.all(promises);

				// Process the results
				results.forEach(result => {
					if (!result) return;

					const { nodeDef, provider, endpointId } = result;

					if (!groupedByProvider[provider]) {
						groupedByProvider[provider] = {};
					}

					if (!groupedByProvider[provider][endpointId]) {
						groupedByProvider[provider][endpointId] = [];
					}

					// Add the regular node def to the grouped structure
					groupedByProvider[provider][endpointId].push(nodeDef);
				});
			}

			// Sort each group by title
			Object.keys(groupedByProvider).forEach((provider: string) => {
				Object.keys(groupedByProvider[provider]).forEach((endpointId: string) => {
					groupedByProvider[provider][endpointId] = _sortBy(groupedByProvider[provider][endpointId], 'title');
				});
			});

			this.groupedNodeDefs = groupedByProvider;
		},

		initializeTokensPerResult () {
			const tokensPerResult: Record<string, Record<string, number>> = {};
			const originalTokensPerResult: Record<string, Record<string, number>> = {};
			const editFlags: Record<string, Record<string, boolean>> = {};
			const dollarsPerResult: Record<string, Record<string, number>> = {};

			// Initialize tokens per result with the first value from each group
			Object.keys(this.groupedNodeDefs).forEach((provider: string) => {
				tokensPerResult[provider] = {};
				originalTokensPerResult[provider] = {};
				editFlags[provider] = {};
				dollarsPerResult[provider] = {};

				Object.keys(this.groupedNodeDefs[provider]).forEach((endpointId: string) => {
					const nodeDefs = this.groupedNodeDefs[provider][endpointId];

					// Default to the first nodeDef's costPerResult
					tokensPerResult[provider][endpointId] = nodeDefs[0]?.costPerResult || 0;
					originalTokensPerResult[provider][endpointId] = nodeDefs[0]?.costPerResult || 0;
					editFlags[provider][endpointId] = false;

					// Calculate the corresponding dollar cost
					dollarsPerResult[provider][endpointId] = this.tokensToDollars(hundredthsToTokens(tokensPerResult[provider][endpointId]));
				});
			});

			this.tokensPerResult = tokensPerResult;
			this.originalTokensPerResult = originalTokensPerResult;
			this.editFlags = editFlags;
			this.dollarsPerResult = dollarsPerResult;
		},

		// Conversion methods
		tokensToDollars (tokens: number): number {
			const costWithMargin: number = new BigNumber(tokens)
				.times(this.INTERNAL_PRICING_RUNNIT_TOKENS_CONVERSION_MULTIPLE)
				.toNumber();
			return new BigNumber(costWithMargin)
				.div(this.INTERNAL_PRICING_RUNNIT_TOKENS_MARGIN_MULTIPLE)
				.toNumber();
		},

		dollarsToTokens (cost: number): number {
			const costWithMargin: number = new BigNumber(cost)
				.times(this.INTERNAL_PRICING_RUNNIT_TOKENS_MARGIN_MULTIPLE)
				.toNumber();
			return Math.round(
				new BigNumber(costWithMargin)
					.div(this.INTERNAL_PRICING_RUNNIT_TOKENS_CONVERSION_MULTIPLE)
					.toNumber(),
			);
		},

		// Bulk edit methods
		onTokensPerResultInput (group: NodeDefGroup, value: number) {
			const { provider, endpointId } = group;

			// Update token value
			this.tokensPerResult = {
				...this.tokensPerResult,
				[provider]: {
					...this.tokensPerResult[provider],
					[endpointId]: (value && tokensToHundredths(value)) || value,
				},
			};

			// Update corresponding dollar value
			this.dollarsPerResult = {
				...this.dollarsPerResult,
				[provider]: {
					...this.dollarsPerResult[provider],
					[endpointId]: this.tokensToDollars(value || 0),
				},
			};

			// Reset individual edit state for all nodes in this group
			if (this.editFlags[provider]?.[endpointId]) {
				group.nodeDefs.forEach((nodeDef: RunnitNodeDef) => {
					if (this.individualNodeDefEdits[nodeDef.id]) {
						this.individualNodeDefEdits = {
							...this.individualNodeDefEdits,
							[nodeDef.id]: {
								...this.individualNodeDefEdits[nodeDef.id],
								editingCost: false,
								editCostValue: undefined,
								hasCostChanges: false,
							},
						};
					}
				});
			}
		},

		onDollarsPerResultInput (group: NodeDefGroup, value: number) {
			const { provider, endpointId } = group;

			// Update the dollars value
			this.dollarsPerResult = {
				...this.dollarsPerResult,
				[provider]: {
					...this.dollarsPerResult[provider],
					[endpointId]: value,
				},
			};

			// Convert to tokens and update
			const tokens: number = this.dollarsToTokens(value);
			this.onTokensPerResultInput(group, tokens);
		},

		// Individual edit methods
		updateNodeDefCost (nodeDef: RunnitNodeDef, value: number) {
			if (!this.individualNodeDefEdits[nodeDef.id]) return;
			const editCostValue: number = tokensToHundredths(value);
			this.individualNodeDefEdits = {
				...this.individualNodeDefEdits,
				[nodeDef.id]: {
					...this.individualNodeDefEdits[nodeDef.id],
					editCostValue,
					hasCostChanges: editCostValue !== nodeDef.costPerResult,
				},
			};
		},

		updateNodeDefDollarCost (nodeDef: RunnitNodeDef, value: number) {
			if (!this.individualNodeDefEdits[nodeDef.id]) return;

			// Store the dollar cost
			this.individualDollarsPerResult[nodeDef.id] = value;

			// Convert to tokens and update the token cost
			const tokens: number = this.dollarsToTokens(value);
			this.updateNodeDefCost(nodeDef, tokens);
		},

		hasCostPerResultLogics (nodeDefs: RunnitNodeDef[]): boolean {
			return nodeDefs.some((nodeDef: RunnitNodeDef) =>
				nodeDef.costPerResultLogics &&
				Array.isArray(nodeDef.costPerResultLogics) &&
				nodeDef.costPerResultLogics.length > 0
			);
		},

		async onSaveAll () {
			if (this.isSaving) return;

			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			try {
				this.isSaving = true;
				let updateCount: number = 0;

				for (const provider in this.tokensPerResult) {
					for (const endpointId in this.tokensPerResult[provider]) {
						if (this.editFlags[provider]?.[endpointId]) {
							const nodeDefs: RunnitNodeDef[] = this.groupedNodeDefs[provider][endpointId];
							const newCostPerResult: number = this.tokensPerResult[provider][endpointId];

							await asyncForEach(nodeDefs, async (nodeDef: RunnitNodeDef) => {
								// Skip if this node has cost per result logics
								if (this.hasCostPerResultLogics([nodeDef])) {
									return;
								}

								// Update costPerResult in Firestore
								await db.collection('runnitNodeDefs')
									.doc(nodeDef.id)
									.update({
										costPerResult: newCostPerResult
									});

								// Update local data
								nodeDef.costPerResult = newCostPerResult;
								updateCount++;
							});

							// Update original values
							this.originalTokensPerResult[provider][endpointId] = newCostPerResult;
						}
					}
				}

				// Write pending publish changes too, otherwise the reload below would discard them
				await asyncForEach(this.nodeDefs, async (nodeDef: RunnitNodeDef) => {
					const editPublishedValue = this.individualNodeDefEdits[nodeDef.id]?.editPublishedValue;
					if (editPublishedValue === undefined) return;

					await db.collection('runnitNodeDefs')
						.doc(nodeDef.id)
						.update({
							isPublished: editPublishedValue,
							publishedAt: editPublishedValue ? FieldValue.serverTimestamp() : null,
						});
					updateCount++;
				});

				// Show success message
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Successfully updated ${updateCount} tools`,
					show: true,
				});
				await this.initialize();
			} catch (e) {
				console.error('Error saving node definitions:', e);

				// Show error message
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error saving tools',
					show: true,
				});
			} finally {
				this.isSaving = false;
			}
		},

		// Logic editor methods
		getLogicOnValueAsString (value: any): string {
			try {
				return JSON.stringify(value);
			} catch (e) {
				return String(value);
			}
		},

		updateLogicOnValue (nodeDef: RunnitNodeDef, logicIndex: number, fieldDefUuid: string, value: string) {
			try {
				// Try to parse as JSON
				const parsedValue = JSON.parse(value);
				this.updateLogicField(nodeDef, logicIndex, fieldDefUuid, 'onValue', parsedValue);
			} catch (e) {
				// If parsing fails, use the string value as is
				this.updateLogicField(nodeDef, logicIndex, fieldDefUuid, 'onValue', value);
			}
		},

		updateLogicOperator (nodeDef: RunnitNodeDef, logicIndex: number, fieldDefUuid: string, value: string) {
			this.updateLogicField(nodeDef, logicIndex, fieldDefUuid, 'operator', value);
		},

		updateLogicOperand (nodeDef: RunnitNodeDef, logicIndex: number, fieldDefUuid: string, value: number) {
			// Get the current operator (including pending edits)
			const operator = this.getCurrentOperatorValue(nodeDef, logicIndex, fieldDefUuid);

			// Only convert tokens to hundredths if the operator is ADD or SET
			const updatedValue: number = operator === 'MULTIPLY'
				? value
				: tokensToHundredths(value);

			// Update the field
			this.updateLogicField(nodeDef, logicIndex, fieldDefUuid, 'operand', updatedValue);
		},

		updateLogicField (nodeDef: RunnitNodeDef, logicIndex: number, fieldDefUuid: string, prop: string, value: any) {
			// Initialize the structure if it doesn't exist
			if (!this.logicEdits[nodeDef.id]) {
				this.logicEdits = {
					...this.logicEdits,
					[nodeDef.id]: {},
				};
			}

			if (!this.logicEdits[nodeDef.id][logicIndex]) {
				this.logicEdits = {
					...this.logicEdits,
					[nodeDef.id]: {
						...this.logicEdits[nodeDef.id],
						[logicIndex]: {},
					},
				};
			}

			// If this is the first change for this field, initialize with current values
			if (!this.logicEdits[nodeDef.id][logicIndex][fieldDefUuid]) {
				const originalLogic = nodeDef.costPerResultLogics[logicIndex].field[fieldDefUuid];
				this.logicEdits = {
					...this.logicEdits,
					[nodeDef.id]: {
						...this.logicEdits[nodeDef.id],
						[logicIndex]: {
							...this.logicEdits[nodeDef.id][logicIndex],
							[fieldDefUuid]: {
								onValue: originalLogic.onValue,
								operator: originalLogic.operator,
								operand: originalLogic.operand,
								saving: false,
								changed: false,
							},
						},
					},
				};
			}

			// Update the specific property
			this.logicEdits = {
				...this.logicEdits,
				[nodeDef.id]: {
					...this.logicEdits[nodeDef.id],
					[logicIndex]: {
						...this.logicEdits[nodeDef.id][logicIndex],
						[fieldDefUuid]: {
							...this.logicEdits[nodeDef.id][logicIndex][fieldDefUuid],
							[prop]: value,
							changed: true,
						},
					},
				},
			};
		},

		hasLogicChanges (nodeDefId: string, logicIndex: number, fieldDefUuid: string): boolean {
			return !!this.logicEdits[nodeDefId]?.[logicIndex]?.[fieldDefUuid]?.changed;
		},

		isLogicSaving (nodeDefId: string, logicIndex: number, fieldDefUuid: string): boolean {
			return !!this.logicEdits[nodeDefId]?.[logicIndex]?.[fieldDefUuid]?.saving;
		},

		isLogicEditDisabled (nodeDef: RunnitNodeDef, logicIndex: number, fieldDefUuid: string): boolean {
			return !!(
				this.loading ||
				this.isSaving ||
				this.isLogicSaving(nodeDef.id, logicIndex, fieldDefUuid)
			);
		},

		// Cost editing methods
		toggleEditingCost (nodeDef: RunnitNodeDef) {
			if (!this.individualNodeDefEdits[nodeDef.id]) {
				this.individualNodeDefEdits = {
					...this.individualNodeDefEdits,
					[nodeDef.id]: {
						editingCost: false,
						editCostValue: undefined,
						hasCostChanges: false,
						editingTime: false,
						editTimeValue: undefined,
						hasTimeChanges: false,
						isSaving: false,
					},
				};
			}

			this.individualNodeDefEdits = {
				...this.individualNodeDefEdits,
				[nodeDef.id]: {
					...this.individualNodeDefEdits[nodeDef.id],
					editingCost: !this.individualNodeDefEdits[nodeDef.id].editingCost,
					editCostValue: nodeDef.costPerResult,
				},
			};
		},

		// Time editing methods
		toggleEditingTime (nodeDef: RunnitNodeDef) {
			if (!this.individualNodeDefEdits[nodeDef.id]) {
				this.individualNodeDefEdits = {
					...this.individualNodeDefEdits,
					[nodeDef.id]: {
						editingCost: false,
						editCostValue: undefined,
						hasCostChanges: false,
						editingTime: false,
						editTimeValue: undefined,
						hasTimeChanges: false,
						isSaving: false,
					},
				};
			}

			this.individualNodeDefEdits = {
				...this.individualNodeDefEdits,
				[nodeDef.id]: {
					...this.individualNodeDefEdits[nodeDef.id],
					editingTime: !this.individualNodeDefEdits[nodeDef.id].editingTime,
					editTimeValue: this._get(nodeDef, 'averageGenInSeconds'),
				},
			};
		},

		updateNodeDefTime (nodeDef: RunnitNodeDef, value: number) {
			if (!this.individualNodeDefEdits[nodeDef.id]) return;

			this.individualNodeDefEdits = {
				...this.individualNodeDefEdits,
				[nodeDef.id]: {
					...this.individualNodeDefEdits[nodeDef.id],
					editTimeValue: value,
					hasTimeChanges: value !== (this._get(nodeDef, 'averageGenInSeconds') || 0),
				},
			};
		},

		// Publish editing methods
		updateNodeDefPublished (nodeDef: RunnitNodeDef, value: boolean) {
			if (!this.individualNodeDefEdits[nodeDef.id]) return;

			this.individualNodeDefEdits = {
				...this.individualNodeDefEdits,
				[nodeDef.id]: {
					...this.individualNodeDefEdits[nodeDef.id],
					// Flipping back to the stored value clears the pending change. Set undefined rather than
					// deleting the key: this spread builds a new object, and reads treat a missing key the same
					editPublishedValue: value !== !!nodeDef.isPublished ? value : undefined,
				},
			};
		},

		cancelEditingCost (nodeDef: RunnitNodeDef) {
			if (!this.individualNodeDefEdits[nodeDef.id]) return;

			this.individualNodeDefEdits = {
				...this.individualNodeDefEdits,
				[nodeDef.id]: {
					...this.individualNodeDefEdits[nodeDef.id],
					editingCost: false,
					editCostValue: undefined,
					hasCostChanges: false
				}
			};
		},

		hasAnyLogicChanges (nodeDef: RunnitNodeDef): boolean {
			if (!nodeDef.costPerResultLogics?.length) return false;

			// Check if any logic has changes
			const nodeDefId: string = nodeDef.id;
			if (!this.logicEdits[nodeDefId]) return false;

			for (const logicIndex in this.logicEdits[nodeDefId]) {
				for (const fieldDefUuid in this.logicEdits[nodeDefId][logicIndex]) {
					if (this.hasLogicChanges(nodeDefId, parseInt(logicIndex), fieldDefUuid)) {
						return true;
					}
				}
			}

			return false;
		},

		async saveNodeDefChanges (nodeDef: RunnitNodeDef) {
			const individualNodeDefEdits = this.individualNodeDefEdits[nodeDef.id];
			if (!individualNodeDefEdits || (!individualNodeDefEdits.hasCostChanges && !individualNodeDefEdits.hasTimeChanges && individualNodeDefEdits.editPublishedValue === undefined && !this.hasAnyLogicChanges(nodeDef))) return;

			try {
				individualNodeDefEdits.isSaving = true;
				const updates: any = {};

				// Handle simple cost changes
				if (individualNodeDefEdits.hasCostChanges && individualNodeDefEdits.editCostValue !== undefined) {
					updates.costPerResult = individualNodeDefEdits.editCostValue;
					nodeDef.costPerResult = individualNodeDefEdits.editCostValue;
				}

				// Handle simple time changes
				if (individualNodeDefEdits.hasTimeChanges && individualNodeDefEdits.editTimeValue !== undefined) {
					updates.averageGenInSeconds = individualNodeDefEdits.editTimeValue;
					this.$set(nodeDef, 'averageGenInSeconds', individualNodeDefEdits.editTimeValue);
				}

				// Handle publish changes
				const { editPublishedValue } = individualNodeDefEdits;
				if (editPublishedValue !== undefined) {
					// Refuse to PUBLISH a tool with no Media tag. Without one it is
					// invisible to the per-media token caps on a team role, which fall
					// back to the role's master cap: a customer hit exactly that and
					// could not run Kling 3.0 Pro while their Video cap sat well above
					// its cost. Unpublishing is always allowed.
					//
					// Trainers are exempt because the token limit never applies to them
					// (see validateTokenLimit in firebase/functions).
					const isTrainer: boolean = nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER;
					// `tagTypes` is the tool's own denormalized record of which tag
					// dimensions it carries, maintained by both writers (the tool editor
					// rebuilds it on save, AdminManageToolTags keeps it in step per tag),
					// so it needs no tag-vocabulary lookup here.
					const hasMediaTypeTag: boolean = !!(nodeDef.tagTypes || {}).MEDIA;
					if (editPublishedValue && !isTrainer && !hasMediaTypeTag) {
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: `${nodeDef.title} needs a Media tag (Image, Video, 3D, and so on) before it can be published. Without one, the per-media token limits on a team role cannot see it.`,
							show: true,
						});
						return;
					}
					updates.isPublished = editPublishedValue;
					updates.publishedAt = editPublishedValue ? FieldValue.serverTimestamp() : null;
				}

				// Apply simple updates if any
				if (Object.keys(updates).length > 0) {
					await db.collection('runnitNodeDefs')
						.doc(nodeDef.id)
						.update(updates);

					// Mirror isPublished only after the write succeeds; publishedAt is stamped by the server
					if (editPublishedValue !== undefined) {
						this.$set(nodeDef, 'isPublished', editPublishedValue);
					}

					this.individualNodeDefEdits = {
						...this.individualNodeDefEdits,
						[nodeDef.id]: {
							...this.individualNodeDefEdits[nodeDef.id],
							hasCostChanges: false,
							hasTimeChanges: false,
							editPublishedValue: undefined,
							editingCost: false,
							editingTime: false
						}
					};
				}

				// Handle logic changes
				if (this.hasAnyLogicChanges(nodeDef)) {
					const nodeDefId: string = nodeDef.id;
					const updates: any = {};

					// If we have logic changes, create a deep copy of the entire costPerResultLogics array
					if (nodeDef.costPerResultLogics && nodeDef.costPerResultLogics.length > 0) {
						const updatedLogics = JSON.parse(JSON.stringify(nodeDef.costPerResultLogics));

						// Apply all logic changes
						for (const logicIndex in this.logicEdits[nodeDefId]) {
							const logicIndexNumber: number = parseInt(logicIndex);

							for (const fieldDefUuid in this.logicEdits[nodeDefId][logicIndex]) {
								if (this.hasLogicChanges(nodeDefId, logicIndexNumber, fieldDefUuid)) {
									const logicEdit = this.logicEdits[nodeDefId][logicIndex][fieldDefUuid];

									// Update the specific field logic in our copy
									updatedLogics[logicIndexNumber].field[fieldDefUuid] = {
										onValue: logicEdit.onValue,
										operator: logicEdit.operator,
										operand: logicEdit.operand
									};

									// Mark as not changed
									this.logicEdits = {
										...this.logicEdits,
										[nodeDefId]: {
											...this.logicEdits[nodeDefId],
											[logicIndex]: {
												...this.logicEdits[nodeDefId][logicIndex],
												[fieldDefUuid]: {
													...this.logicEdits[nodeDefId][logicIndex][fieldDefUuid],
													changed: false,
												},
											},
										},
									};
								}
							}
						}

						updates.costPerResultLogics = updatedLogics;
					}

					// Only update if we have changes
					if (Object.keys(updates).length > 0) {
						await db.collection('runnitNodeDefs')
							.doc(nodeDefId)
							.update(updates);

						// Update local data if we changed the logics
						if (updates.costPerResultLogics) {
							nodeDef.costPerResultLogics = updates.costPerResultLogics;
						}
					}
				}

				// Show success message
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Successfully updated ${nodeDef.title}`,
					show: true,
				});
			} catch (e: any) {
				console.error('Error saving node definition:', e);

				// Show error message
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error saving: ${e.message || 'Unknown error'}`,
					show: true
				});
			} finally {
				this.individualNodeDefEdits = {
					...this.individualNodeDefEdits,
					[nodeDef.id]: {
						...this.individualNodeDefEdits[nodeDef.id],
						isSaving: false,
					},
				};
			}
		},

		isBulkEditEnabled (nodeDef: RunnitNodeDef): boolean {
			// Find the provider and endpointId for this nodeDef
			for (const provider in this.groupedNodeDefs) {
				for (const endpointId in this.groupedNodeDefs[provider]) {
					// Check if this nodeDef is in the current group
					const isInGroup: boolean = this.groupedNodeDefs[provider][endpointId].some(
						(def: RunnitNodeDef) => def.id === nodeDef.id
					);

					// If it's in this group, check if the bulk edit is enabled
					if (isInGroup) {
						return !!this.editFlags[provider]?.[endpointId];
					}
				}
			}

			return false;
		},

		toggleBulkEdit (group: NodeDefGroup, value: boolean) {
			const { provider, endpointId } = group;

			// Update editFlags state
			this.editFlags = {
				...this.editFlags,
				[provider]: {
					...this.editFlags[provider],
					[endpointId]: value,
				}
			};

			// If bulk edit is enabled, reset individual edit states
			if (value) {
				group.nodeDefs.forEach(nodeDef => {
					if (this.individualNodeDefEdits[nodeDef.id]) {
						this.individualNodeDefEdits = {
							...this.individualNodeDefEdits,
							[nodeDef.id]: {
								...this.individualNodeDefEdits[nodeDef.id],
								editingCost: false,
								editCostValue: undefined,
								hasCostChanges: false,
							}
						};
					}
				});
			}
		},

		getCurrentOperatorValue (nodeDef: RunnitNodeDef, logicIndex: number, fieldDefUuid: string): string {
			// First check if there's a pending edit for this field
			if (this.logicEdits[nodeDef.id]?.[logicIndex]?.[fieldDefUuid]?.operator) {
				return this.logicEdits[nodeDef.id][logicIndex][fieldDefUuid].operator;
			}

			// Fall back to the original operator
			if (nodeDef.costPerResultLogics && nodeDef.costPerResultLogics.length > 0) {
				const logic = nodeDef.costPerResultLogics[logicIndex].field[fieldDefUuid];
				return logic.operator;
			}
			return null;
		},
	}
});
